import * as React from 'react';
// mui
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
// icons
import RestartAltIcon from '@mui/icons-material/RestartAlt';
// redux
import { useSelector } from 'react-redux';
import { selectRecurringTaskById } from '../../../store/selectors/recuringTaskSelectors';
// dayjs
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
// types
import RecurringTask from '../../../interfaces/RecurringTask.interface';
// components
import TaskListRecurringItemContent from './RecurringTaskItemContent';
import RecurringTaskDialog from './RecurringTaskDialog';
import RecurringTaskItemActions from './RecurringTaskItemActions';

interface RecurringTaskListItemProps {
  padding: number;
  parentTaskId: number | null;
  recurringTaskId: number;
  showProject?: boolean;
}

export default function RecurringTaskItem(props: RecurringTaskListItemProps) {
  // hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // props
  const recurringTaskId: number = props.recurringTaskId;
  const inheritedPadding: number = isMobile ? props.padding / 2 : props.padding;
  const parentTaskId: number | null = props.parentTaskId;
  const showProject = props.showProject || false;

  // redux
  const localRecurringTask: RecurringTask = useSelector(selectRecurringTaskById(recurringTaskId)) as RecurringTask;

  // local state
  const [ itemIsHovered, setItemIsHovered ] = React.useState<boolean>(false);

  // edit recurring task dialog
  const [ editRecurringTaskDialogStatus, setEditRecurringTaskDialogStatus ] = React.useState(false);
  
  const openEditDialog = () => setEditRecurringTaskDialogStatus(true);
  const closeEditDialog = () => setEditRecurringTaskDialogStatus(false);

  const handleEditDialogSave = () => {
    setEditRecurringTaskDialogStatus(false);
  }

  return (
    <Box>
      <ListItemButton
        sx={{ pl: inheritedPadding+1, pr: isMobile ? 1 : '' }}
        onMouseEnter={() => setItemIsHovered(true)}
        onMouseLeave={() => setItemIsHovered(false)}
        onClick={openEditDialog}
      >
        {/* dropdown button with dynamic icon */}
        <ListItemIcon
          sx={{ minWidth: isMobile ? 36 : 46, color: 'lightgrey' }}
        >
          <IconButton
            sx={{ p: isMobile ? 1 : '' }}
            disabled={localRecurringTask.completed || localRecurringTask.permanentlyCompleted} // this is just to change the color
            disableRipple // this stops it from functioning like a button
          >
            <RestartAltIcon />
          </IconButton>
        </ListItemIcon>

        {/* task information */}
        <TaskListRecurringItemContent
          recurringTaskId={localRecurringTask.id}
          showProject={showProject}
        />

        {/* task actions */}
        <RecurringTaskItemActions
          taskId={localRecurringTask.id}
          parentTaskId={parentTaskId}
          isHovered={itemIsHovered}
        />
      </ListItemButton>
      <Divider sx={{ ml: (isMobile ? inheritedPadding+1 : inheritedPadding), mr: isMobile ? '8px' : 'auto' }} />

      {/* dialog for editing */}
      { editRecurringTaskDialogStatus &&
        <RecurringTaskDialog
          parentTaskId={parentTaskId}
          onSave={handleEditDialogSave}
          onCancel={closeEditDialog}
          recurringTaskId={localRecurringTask.id}
          addMode={false}
        />
      }
    </Box>
  );
}
